import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LocationOffOutlinedIcon from '@mui/icons-material/LocationOffOutlined';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import BaseMap, { pinIcon } from './BaseMap';
import { CRS, CRS_ORDER, fromLatLng, guessCrs, splitPair, toLatLng } from '../../utils/geo';

/**
 * Optional job location. Three ways in: click/drag on the map, the device's current position,
 * or typed/pasted coordinates (GPS lat/lng, Gauss-Krüger zone 6 or MONREF97 / UTM 34N).
 * `value` is { lat, lng } or null.
 */
/** `fill`: the map takes all the height of its container (used by the full-screen location dialog). */
export default function LocationPicker({ value, onChange, fill }) {
    const { t } = useTranslation();
    const mapRef = useRef(null);
    const markerRef = useRef(null);
    const [crs, setCrs] = useState(() => {
        try { return localStorage.getItem('SD_CRS') || 'WGS84'; } catch { return 'WGS84'; }
    });
    const [a, setA] = useState('');
    const [b, setB] = useState('');
    const [error, setError] = useState(null);
    const [locating, setLocating] = useState(false);
    const [full, setFull] = useState(false);

    // Full screen: the same map grows over the whole window (marker and zoom are kept).
    useEffect(() => {
        const id = setTimeout(() => mapRef.current?.invalidateSize(), 50);
        if (!full) return () => clearTimeout(id);
        const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
        window.addEventListener('keydown', onKey);
        return () => { clearTimeout(id); window.removeEventListener('keydown', onKey); };
    }, [full]);

    // Show the current point in the chosen system.
    const fillFields = useCallback((point, system) => {
        if (!point) { setA(''); setB(''); return; }
        const [x, y] = fromLatLng(system, point.lat, point.lng);
        setA(x); setB(y);
    }, []);

    const placeMarker = useCallback((point, pan) => {
        const map = mapRef.current;
        if (!map) return;
        if (!point) {
            if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
            return;
        }
        if (!markerRef.current) {
            markerRef.current = L.marker([point.lat, point.lng], { draggable: true, icon: pinIcon('#EF4444', 22) }).addTo(map);
            markerRef.current.on('dragend', (e) => {
                const p = e.target.getLatLng();
                onChangeRef.current({ lat: round7(p.lat), lng: round7(p.lng) });
            });
        } else {
            markerRef.current.setLatLng([point.lat, point.lng]);
        }
        if (pan) map.setView([point.lat, point.lng], Math.max(map.getZoom(), 17));
    }, []);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        placeMarker(value, false);
        fillFields(value, crs);
    }, [value?.lat, value?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

    const onReady = (map) => {
        mapRef.current = map;
        if (value) {
            map.setView([value.lat, value.lng], 17);
            placeMarker(value, false);
        }
        map.on('click', (e) => onChangeRef.current({ lat: round7(e.latlng.lat), lng: round7(e.latlng.lng) }));
    };

    const changeCrs = (next) => {
        setCrs(next);
        try { localStorage.setItem('SD_CRS', next); } catch { /* ignore */ }
        setError(null);
        fillFields(value, next);
    };

    const apply = (system = crs, x = a, y = b) => {
        if (!String(x).trim() && !String(y).trim()) return;
        const r = toLatLng(system, x, y);
        if (r.error) { setError(t(r.error)); return; }
        setError(null);
        onChange(r);
        placeMarker(r, true);
    };

    // Pasting "Y X" or "lat, lng" into the first field fills both and detects the system.
    const onPasteFirst = (e) => {
        const pair = splitPair(e.clipboardData.getData('text'));
        if (!pair) return;
        e.preventDefault();
        const guessed = guessCrs(pair[0], pair[1]) || crs;
        setCrs(guessed);
        setA(pair[0]); setB(pair[1]);
        apply(guessed, pair[0], pair[1]);
    };

    const locate = () => {
        if (!navigator.geolocation || !window.isSecureContext) { setError(t('location.errors.geoUnavailable')); return; }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLocating(false); setError(null);
                const p = { lat: round7(pos.coords.latitude), lng: round7(pos.coords.longitude) };
                onChange(p);
                placeMarker(p, true);
            },
            () => { setLocating(false); setError(t('location.errors.geoDenied')); },
            { enableHighAccuracy: true, timeout: 15000 },
        );
    };

    const sys = CRS[crs];
    return (
        <Box sx={fill ? { display: 'flex', flexDirection: 'column', height: '100%' } : undefined}>
            <Box sx={full
                ? { position: 'fixed', inset: 0, zIndex: 1500, bgcolor: '#fff', display: 'flex', flexDirection: 'column' }
                : { position: 'relative', ...(fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : {}) }}>
                {full && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, pt: 'calc(10px + env(safe-area-inset-top, 0px))', borderBottom: '1px solid #E2E8F0' }}>
                        <Typography sx={{ flex: 1, fontSize: 13, color: '#64748B' }}>{t('location.mapHint')}</Typography>
                        <Button variant="contained" disableElevation onClick={() => setFull(false)}>{t('location.done')}</Button>
                    </Box>
                )}
                <Box sx={{ position: 'relative', flex: full || fill ? 1 : 'none', minHeight: 0 }}>
                    <BaseMap height={full || fill ? '100%' : 380} onReady={onReady} center={value ? [value.lat, value.lng] : undefined} zoom={value ? 17 : undefined}
                             sx={full ? { borderRadius: 0, border: 0 } : undefined} />
                    {!full && !fill && (
                        <Tooltip title={t('location.fullScreen')} placement="right">
                            <IconButton onClick={() => setFull(true)} aria-label={t('location.fullScreen')} size="small" sx={{
                                position: 'absolute', left: 10, bottom: 10, zIndex: 1000, bgcolor: '#fff', border: '2px solid rgba(0,0,0,0.2)',
                                borderRadius: '4px', '&:hover': { bgcolor: '#F4F4F4' },
                            }}><OpenInFullIcon sx={{ fontSize: 18 }} /></IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>
            {!fill && <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.75 }}>{t('location.mapHint')}</Typography>}

            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField select size="small" label={t('location.system')} value={crs} onChange={(e) => changeCrs(e.target.value)} sx={{ minWidth: 190, flex: '1 1 190px' }}>
                    {CRS_ORDER.map(k => <MenuItem key={k} value={k}>{t(`location.crs.${k}`)}</MenuItem>)}
                </TextField>
                <TextField size="small" label={t(`location.axis.${sys.first}`)} value={a} onChange={(e) => setA(e.target.value)} onPaste={onPasteFirst}
                           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
                           sx={{ flex: '1 1 120px' }} inputProps={{ inputMode: 'decimal' }} />
                <TextField size="small" label={t(`location.axis.${sys.second}`)} value={b} onChange={(e) => setB(e.target.value)}
                           onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
                           sx={{ flex: '1 1 120px' }} inputProps={{ inputMode: 'decimal' }} />
                <Button variant="outlined" startIcon={<PlaceOutlinedIcon />} onClick={() => apply()} sx={{ height: 40 }}>{t('location.show')}</Button>
            </Box>
            {error && <Typography sx={{ fontSize: 12.5, color: '#DC2626', mt: 0.75 }}>{error}</Typography>}
            {crs !== 'WGS84' && <Typography sx={{ fontSize: 11.5, color: '#94A3B8', mt: 0.5 }}>{t('location.accuracyNote')}</Typography>}

            <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
                <Button size="small" startIcon={locating ? <CircularProgress size={14} /> : <MyLocationIcon />} onClick={locate} disabled={locating}>
                    {t('location.useMine')}
                </Button>
                {value && (
                    <Button size="small" color="inherit" startIcon={<LocationOffOutlinedIcon />} onClick={() => { onChange(null); setError(null); }} sx={{ color: '#64748B' }}>
                        {t('location.remove')}
                    </Button>
                )}
            </Box>
        </Box>
    );
}

const round7 = (v) => Math.round(v * 1e7) / 1e7;
